import React, { useState, useRef, useEffect } from 'react';

const EditableTextCell = ({ value, onSave, placeholder = '' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const inputRef = useRef(null);
  const divRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value ?? '');
    }
  }, [value, isEditing]);

  useEffect(() => {
    const el = divRef.current;
    if (!el) return;
    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const startEdit = () => {
    setLocalValue(value ?? '');
    setIsEditing(true);
  };

  const save = async () => {
    setIsEditing(false);
    const trimmed = localValue.trim();
    const prevValue = value ?? '';
    if (trimmed !== prevValue && onSave) {
      setIsSaving(true);
      try {
        await onSave(trimmed);
      } catch {
        // onSave errors are handled by the caller
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    }
    if (e.key === 'Escape') {
      setLocalValue(value ?? '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type='text'
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={save}
        onKeyDown={handleKeyDown}
        className='crm-table-editable-text-input'
      />
    );
  }

  return (
    <div
      ref={divRef}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEdit();
        }
      }}
      role='button'
      tabIndex={0}
      className='crm-table-editable-text'
      {...(isTruncated && value && {
        'data-tooltip-content': value,
        'data-tooltip-id': 'tooltip-mobile',
        'data-tooltip-class-name': 'header-tooltip pricing-tooltip-description pricing-tooltip-description',
      })}
    >
      {isSaving ? (
        <i className='fas fa-spinner fa-spin d-inline-block-centered' />
      ) : value ? (
        <span>{value}</span>
      ) : (
        <span className='crm-table-editable-text-placeholder'>{placeholder}</span>
      )}
    </div>
  );
};

export default EditableTextCell;
